import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ErrorBanner, FormField, PasswordFields, usePasswordValidation } from '@/components';
import EurodimaLogo from '@/components/assets/EurodimaLogo';
import { authApi, isAuthError } from '@/api';

function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const passwordValidation = usePasswordValidation(password, confirmPassword);
  const canSubmit = username.trim() && passwordValidation.allRequirementsMet && passwordValidation.passwordsMatch;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsLoading(true);
    setError(null);

    try {
      await authApi.acceptInvite({ username: username.trim(), password });
      setSuccess(true);
      setTimeout(() => {
        navigate({ to: '/' });
      }, 2000);
    } catch (err) {
      if (isAuthError(err)) {
        setError(err.message);
      } else {
        setError(t('auth.acceptInviteError', 'Failed to accept invite. Please try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-background">
      <Card>
        <div className="flex justify-center mb-8">
          <div className="w-48">
            <EurodimaLogo />
          </div>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-onsurface">
            {t('auth.acceptInvite', 'Accept Invitation')}
          </h1>
          <p className="mt-2 text-sm text-muted">
            {t('auth.acceptInviteSubtitle', 'Set up your account to get started')}
          </p>
        </div>

        {success ? (
          <div className="text-center">
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              {t('auth.acceptInviteSuccess', 'Account set up successfully. Redirecting...')}
            </div>
          </div>
        ) : (
          <>
            <ErrorBanner hasError={!!error}>
              {error}
            </ErrorBanner>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <FormField
                  label={t('auth.username', 'Username')}
                  type="text"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="johndoe"
                  required
                  autoComplete="username"
                />
              </div>

              <PasswordFields
                password={password}
                confirmPassword={confirmPassword}
                onPasswordChange={setPassword}
                onConfirmPasswordChange={setConfirmPassword}
                confirmPasswordTouched={confirmPasswordTouched}
                onConfirmPasswordBlur={() => setConfirmPasswordTouched(true)}
              />

              <button
                type="submit"
                disabled={isLoading || !canSubmit}
                className="w-full py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-primary text-onprimary hover:bg-primary-darker focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                {isLoading ? (
                  <span className="animate-spin inline-block size-4 border-2 border-current border-t-transparent rounded-full" />
                ) : null}
                {t('auth.acceptInviteButton', 'Complete Setup')}
              </button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/_auth/accept-invite')({
  component: AcceptInvitePage,
});
